import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { LtModal, LeftNavigationBar } from 'limetray-ui-kit';
import LtLoader from '../../LtLoader/index';
import './ManagerModal.scss';

const SCROLL_OFFSET = 20;
const ACTIVE_OFFSET = 120;

/**
 * ManagerModal – Reusable modal layout with a left navigation bar (categories +
 * subcategories) and a scrollable right content pane.
 *
 * Mirrors the OutletCreateModal layout pattern so it can be reused across
 * UserCreateEditModal, RoleCreateEditModal, etc.
 *
 * Props:
 * ─────────────────────────────────────────────────────────────────────────────
 * open          {boolean}  – Whether the modal is visible
 * onClose       {function} – Called when the modal requests close
 * header        {string}   – Modal header title
 * onSave        {function} – Called when the save icon/button is clicked
 * isLoading     {boolean}  – When true a loading overlay is shown (default false)
 * loadingMessage {string}  – Message shown alongside the loader
 * className     {string}   – Extra className forwarded to LtModal root
 *
 * navConfig     {Array}    – Navigation tree with categories + subcategories:
 *   [
 *     {
 *       id: number,
 *       name: string,
 *       slug: string,
 *       subcategories: [{ id: number, name: string, slug: string }]
 *     }
 *   ]
 *
 * sections      {object}   – Map of slug → section descriptor:
 *   {
 *     [slug]: {
 *       outerTitle: string,            // shown above the section card
 *       innerTitle?: string,           // optional, shown inside the card header
 *       component: ReactNode,          // section body content
 *     }
 *   }
 *
 * defaultSlug   {string}   – Slug to activate when modal opens
 *
 * toast         {object|null} – { id, variant, title, description }
 * onToastClose  {function} – Called when toast closes
 *
 * hashPrefix    {string}   – Prefix for LeftNavigationBar hash (default 'mgr')
 */
function ManagerModal({
  open,
  onClose,
  header,
  onSave,
  isLoading = false,
  loadingMessage = 'Loading...',
  className = '',
  navConfig = [],
  sections = {},
  defaultSlug = '',
  hashPrefix = 'mgr',
}) {
  const firstSubSlug = useMemo(() => {
    if (defaultSlug) return defaultSlug;
    for (const cat of navConfig) {
      if (cat.subcategories?.length) return cat.subcategories[0].slug;
      if (cat.slug) return cat.slug;
    }
    return '';
  }, [navConfig, defaultSlug]);

  const [activeSlug, setActiveSlug] = useState(firstSubSlug);
  const rightPaneRef = useRef(null);

  // Build a lookup: subcategory id → { slug, parentSlug }
  const slugLookup = useMemo(() => {
    const map = new Map();
    navConfig.forEach((category) => {
      if (category.slug) {
        map.set(category.id, { slug: category.slug });
      }
      category.subcategories?.forEach((sub) => {
        if (sub.slug) {
          map.set(sub.id, { slug: sub.slug, parentSlug: category.slug });
        }
      });
    });
    return map;
  }, [navConfig]);

  // Reset active slug when modal opens
  useEffect(() => {
    if (open) {
      setActiveSlug(firstSubSlug);
    }
  }, [open, firstSubSlug]);

  // Smooth scroll to a section in the right pane
  const scrollToSection = useCallback((slug) => {
    const container = rightPaneRef.current;
    if (!container) return;
    const target = container.querySelector(`#${CSS.escape(slug)}`);
    if (!target) return;
    const top = Math.max(target.offsetTop - SCROLL_OFFSET, 0);
    container.scrollTo({ top, behavior: 'smooth' });
  }, []);

  // Sync active slug with scroll position (mirrors OutletCreateModal pattern)
  useEffect(() => {
    if (!open) return;
    const container = rightPaneRef.current;
    if (!container) return;

    const handleScroll = () => {
      const sectionEls = container.querySelectorAll('section[id]');
      if (!sectionEls.length) return;
      const containerTop = container.getBoundingClientRect().top;
      let current = sectionEls[0].id;
      sectionEls.forEach((el) => {
        const top = el.getBoundingClientRect().top - containerTop;
        if (top <= ACTIVE_OFFSET) {
          current = el.id;
        }
      });
      setActiveSlug(current);
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, [open]);

  // LeftNavigationBar click: subcategoryId is the sub being clicked;
  // when a category header (no sub) is clicked, categoryId is passed.
  const handleCategoryClick = (_categoryId, subcategoryId) => {
    if (!subcategoryId) return; // top-level category click — no scroll
    const slugData = slugLookup.get(subcategoryId);
    if (!slugData) return;
    setActiveSlug(slugData.slug);
    scrollToSection(slugData.slug);
  };

  // Ordered list of section slugs to render (from navConfig subcategory order)
  const orderedSlugs = useMemo(() => {
    const result = [];
    navConfig.forEach((cat) => {
      if (cat.subcategories?.length) {
        cat.subcategories.forEach((sub) => result.push(sub.slug));
      } else if (cat.slug) {
        result.push(cat.slug);
      }
    });
    return result;
  }, [navConfig]);

  return (
    <LtModal
      open={open}
      onClose={onClose}
      header={header}
      showSaveIcon
      onSave={onSave}
      className={`manager-modal ${className}`}
    >
      <div className="manager-modal-body">
        {/* Loading overlay */}
        {isLoading && (
          <div className="manager-modal-loading-overlay">
            <LtLoader size="large" inline="centered" message={loadingMessage} />
          </div>
        )}

        {/* Left navigation */}
        <div className="manager-modal-left-nav">
          <LeftNavigationBar
            itemList={navConfig}
            selectedSubcategorySlug={activeSlug}
            onCategoryClick={handleCategoryClick}
            categoryHashPrefix={`${hashPrefix}-cat-`}
            subcategoryHashPrefix={`${hashPrefix}-sub-`}
            showBackButton={false}
            isCollapsable
          />
        </div>

        {/* Right scrollable content */}
        <div
          className="manager-modal-content"
          ref={rightPaneRef}
        >
          {orderedSlugs.map((slug) => {
            const sectionDef = sections[slug];
            if (!sectionDef) return null;
            return (
              <section key={slug} id={slug}>
                {/* Outer section title — above the card */}
                {sectionDef.outerTitle && (
                  <p className="manager-section-title-outer">
                    {sectionDef.outerTitle}
                  </p>
                )}

                {/* Section card */}
                <div className="manager-section-card">
                  {/* Inner section title — inside the card header */}
                  {sectionDef.innerTitle && (
                    <p className="manager-section-title-inner">
                      {sectionDef.innerTitle}
                    </p>
                  )}
                  {sectionDef.component}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </LtModal>
  );
}

export default ManagerModal;
